import { FunctionComponent, useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useGetListQuery } from "@/pages/Private/redux/list/list.api";
import { LoadingOverlay, OutputLanguageSelect } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { Button } from "@/components/Button/Button";
import { StepInfo } from "@/components/StepInfo/StepInfo";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { TextArea } from "@/components/TextArea/TextArea";
import { Toggle } from "@/components/Toggle/Toggle";

import {
	useGetSignalConfigQuery,
	useUpdateSignalConfigMutation,
	useGetCompanySignalConfigQuery,
	useTestSignalConfigMutation,
} from "@/pages/Private/redux/signal/signal.api";

import { PriorityDefinitionBoard } from "@/pages/Private/pages/Signals/components/PriorityDefinitions/PriorityDefinitionBoard";

import { ListBuilderLayout } from "../../components/ListBuilderLayout";

import { TestSignalConfigResponse } from "../../../Signals/schema/signal";
import { TestSignalConfigModal } from "../../../Signals/components/TestSignalConfigModal";

import { getAvailableSteps, isDetailsNeeded, getDoneSteps } from "../../utils/create-list-steps";
import { Stepper } from "../ListEditDetails/components/Stepper";
import { FeaturesCodes } from "../../../Users/enum/codes";
import {
	ListSignalConfig,
	SignalPriorityItem,
	SignalPriorityLevel,
	CreateSignalPriorityItem,
} from "../../schema/list";
import { PrioritySection } from "./components/PrioritySection";
import { PriorityItemModal } from "./components/PriorityItemModal";

// Default signal config for new lists
const getDefaultSignalConfig = (listId: number): ListSignalConfig => ({
	listId,
	deepResearchPrompt: "",
	leadUpdatesPrompt: "",
	companyUpdatesPrompt: "",
	industryUpdatesPrompt: "",
	systemPrompt: "",
	checkLinkedinPosts: true,
	analyzeLinkedinEngagement: true,
	checkTechStackChanges: true,
	language: null,
	priorities: [],
});

export const ListEditSignals: FunctionComponent = () => {
	const { t } = useTranslation();
	const { id = 0 } = useParams();
	const navigate = useNavigate();
	const ts = useCallback((key: string) => t(`list.${key}`), [t]);

	const { data, isFetching } = useGetListQuery(+id);
	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	// API hooks
	const { data: signalConfigData, isFetching: isSignalConfigFetching } = useGetSignalConfigQuery(
		+id,
		{ skip: !id }
	);
	const { data: companySignalConfigData, isFetching: isCompanySignalConfigFetching } =
		useGetCompanySignalConfigQuery();
	const [updateSignalConfig, { isLoading: isSaving }] = useUpdateSignalConfigMutation();
	const [testSignalConfig, { isLoading: isTesting }] = useTestSignalConfigMutation();

	// Test modal state
	const [testModalOpen, setTestModalOpen] = useState(false);
	const [testResult, setTestResult] = useState<TestSignalConfigResponse | null>(null);
	const [testError, setTestError] = useState<string | null>(null);

	// Local state for signal configuration
	const [signalConfig, setSignalConfig] = useState<ListSignalConfig>(() =>
		getDefaultSignalConfig(+id)
	);
	// Snapshot of the last persisted/loaded config used to derive dirty state
	const [savedConfig, setSavedConfig] = useState<ListSignalConfig | null>(null);

	// Helper to check if signal config has any meaningful values set
	const isSignalConfigEmpty = (config: ListSignalConfig): boolean => {
		return (
			!config.deepResearchPrompt &&
			!config.systemPrompt &&
			!config.leadUpdatesPrompt &&
			!config.companyUpdatesPrompt &&
			!config.industryUpdatesPrompt &&
			!config.checkLinkedinPosts &&
			!config.analyzeLinkedinEngagement &&
			!config.checkTechStackChanges &&
			!config.language &&
			config.priorities.length === 0
		);
	};

	// Sync API data to local state, using company signals as default when list signals are empty
	useEffect(() => {
		if (signalConfigData) {
			// If list has no signals set and company signals exist, use company signals as default
			if (isSignalConfigEmpty(signalConfigData) && companySignalConfigData) {
				const merged: ListSignalConfig = {
					listId: +id,
					deepResearchPrompt: companySignalConfigData.deepResearchPrompt || "",
					systemPrompt: companySignalConfigData.systemPrompt || "",
					leadUpdatesPrompt: companySignalConfigData.leadUpdatesPrompt || "",
					companyUpdatesPrompt: companySignalConfigData.companyUpdatesPrompt || "",
					industryUpdatesPrompt: companySignalConfigData.industryUpdatesPrompt || "",
					checkLinkedinPosts: companySignalConfigData.checkLinkedinPosts || true,
					analyzeLinkedinEngagement: companySignalConfigData.analyzeLinkedinEngagement || true,
					checkTechStackChanges: companySignalConfigData.checkTechStackChanges || true,
					language: companySignalConfigData.language ?? null,
					priorities: companySignalConfigData.priorities.map((p) => ({
						...p,
						id: undefined, // Remove IDs so new ones are created for the list
					})),
				};

				setSignalConfig(merged);
				setSavedConfig(merged);
			} else {
				setSignalConfig(signalConfigData);
				setSavedConfig(signalConfigData);
			}
		}
	}, [signalConfigData, companySignalConfigData, id]);

	// Modal state
	const [modalOpen, setModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<SignalPriorityItem | null>(null);
	const [editingPriorityLevel, setEditingPriorityLevel] = useState<SignalPriorityLevel>(
		SignalPriorityLevel.PRIORITY_1
	);
	const [modalKey, setModalKey] = useState(0);

	// Priority item handlers
	const handleAddPriorityItem = (priorityLevel: SignalPriorityLevel) => {
		setEditingPriorityLevel(priorityLevel);
		setEditingItem(null);
		setModalKey((prev) => prev + 1);
		setModalOpen(true);
	};

	const handleEditPriorityItem = (item: SignalPriorityItem) => {
		setEditingPriorityLevel(item.priorityLevel);
		setEditingItem(item);
		setModalKey((prev) => prev + 1);
		setModalOpen(true);
	};

	const handleDeletePriorityItem = (item: SignalPriorityItem) => {
		setSignalConfig((prev) => ({
			...prev,
			priorities: prev.priorities.filter(
				(p) => !(p.title === item.title && p.priorityLevel === item.priorityLevel)
			),
		}));
	};

	const handleSavePriorityItem = (item: CreateSignalPriorityItem & { id?: number }) => {
		setSignalConfig((prev) => {
			const existingIndex = prev.priorities.findIndex(
				(p) =>
					p.id === item.id ||
					(p.title === editingItem?.title && p.priorityLevel === editingItem?.priorityLevel)
			);

			if (existingIndex >= 0) {
				// Update existing
				const updated = [...prev.priorities];

				updated[existingIndex] = { ...updated[existingIndex], ...item };

				return { ...prev, priorities: updated };
			} else {
				// Add new
				return {
					...prev,
					priorities: [...prev.priorities, { ...item, id: Date.now() }],
				};
			}
		});
	};

	// PREC-3908: a card dragged into another priority box. The board hands back the whole
	// reordered array, so this only has to store it — and only fires for a real move.
	const handleMovePriorityItem = (priorities: SignalPriorityItem[]) => {
		setSignalConfig((prev) => ({ ...prev, priorities }));
	};

	const getPriorityItems = (level: SignalPriorityLevel) =>
		signalConfig.priorities.filter((p) => p.priorityLevel === level);

	const handleTest = async () => {
		setTestResult(null);
		setTestError(null);
		setTestModalOpen(true);

		try {
			const prioritiesWithOrder = signalConfig.priorities.map((p, idx) => ({
				...(p.id ? { id: p.id } : {}),
				priorityLevel: p.priorityLevel,
				title: p.title,
				definition: p.definition,
				examples: p.examples,
				order: p.order ?? idx,
			}));

			const result = await testSignalConfig({
				signalConfig: {
					deepResearchPrompt: signalConfig.deepResearchPrompt,
					systemPrompt: signalConfig.systemPrompt,
					leadUpdatesPrompt: signalConfig.leadUpdatesPrompt,
					companyUpdatesPrompt: signalConfig.companyUpdatesPrompt,
					industryUpdatesPrompt: signalConfig.industryUpdatesPrompt,
					language: signalConfig.language,
					priorities: prioritiesWithOrder,
				},
			}).unwrap();

			setTestResult(result);
		} catch (err) {
			setTestError(err instanceof Error ? err.message : String(err));
		}
	};

	// Build the normalized payload that gets sent to the API
	const buildSavePayload = useCallback((config: ListSignalConfig) => {
		const prioritiesWithOrder = config.priorities.map((p, idx) => ({
			...(p.id ? { id: p.id } : {}),
			priorityLevel: p.priorityLevel,
			title: p.title,
			definition: p.definition,
			examples: p.examples,
			order: p.order ?? idx,
		}));

		return {
			deepResearchPrompt: config.deepResearchPrompt,
			systemPrompt: config.systemPrompt,
			leadUpdatesPrompt: config.leadUpdatesPrompt,
			companyUpdatesPrompt: config.companyUpdatesPrompt,
			industryUpdatesPrompt: config.industryUpdatesPrompt,
			checkLinkedinPosts: config.checkLinkedinPosts,
			analyzeLinkedinEngagement: config.analyzeLinkedinEngagement,
			checkTechStackChanges: config.checkTechStackChanges,
			language: config.language,
			priorities: prioritiesWithOrder,
		};
	}, []);

	// Dirty state derived from comparing the normalized payloads
	const isDirty = useMemo(() => {
		if (!savedConfig) {
			return false;
		}

		return (
			JSON.stringify(buildSavePayload(signalConfig)) !==
			JSON.stringify(buildSavePayload(savedConfig))
		);
	}, [signalConfig, savedConfig, buildSavePayload]);

	const persistSignalConfig = async () => {
		await updateSignalConfig({
			listId: +id,
			data: buildSavePayload(signalConfig),
		}).unwrap();

		setSavedConfig(signalConfig);
	};

	const handleSaveOnly = async () => {
		try {
			await persistSignalConfig();
		} catch (error) {
			console.error("Error saving signal config:", error);
		}
	};

	const handleSave = async () => {
		try {
			await persistSignalConfig();

			// Next is the tracker's Find person step (PREC-4399).
			navigate(`/app/lists/create/details/${id}`);
		} catch (error) {
			console.error("Error saving signal config:", error);
		}
	};

	if (isFetching || isSignalConfigFetching || isCompanySignalConfigFetching) {
		return <LoadingOverlay />;
	}

	if (!data) {
		return <div>List not found</div>;
	}

	return (
		<ListBuilderLayout sourceTypeLabel={ts(data.sourceType)}>
			<Stepper
				availableSteps={getAvailableSteps(isDetailsNeeded(data), data, isSequencerActive)}
				currentStep="signals"
				doneSteps={getDoneSteps(data)}
				listId={data.id}
			/>
			<div className="w-full">
				<StepInfo
					description={
						<p>
							Configure signals for your tracker list. Signals help you identify when tracked
							profiles or companies have activity that may indicate buying intent or engagement
							opportunities.
						</p>
					}
					title={t("list.signals.title")}
				/>

				{/* AI Prompts Section */}
				<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
					<h3 className="text-sm font-semibold mb-4">{t("list.signals.aiPrompts")}</h3>
					<div className="space-y-4">
						<TextArea
							handleChange={(e) =>
								setSignalConfig((prev) => ({ ...prev, deepResearchPrompt: e.target.value }))
							}
							label={t("list.signals.deepResearchPrompt")}
							name="deepResearchPrompt"
							placeholder={t("list.signals.deepResearchPromptPlaceholder")}
							showError={false}
							value={signalConfig.deepResearchPrompt}
						/>
						<div className="max-w-[320px]">
							<OutputLanguageSelect
								handleSelect={(language) => setSignalConfig((prev) => ({ ...prev, language }))}
								label={t("list.signals.outputLanguage")}
								value={signalConfig.language}
							/>
						</div>
					</div>
				</div>

				{/* Signal Categorization Section */}
				<div className="p-4 mt-6 rounded-xl border border-gray-200 bg-brand-white">
					<h3 className="text-sm font-semibold mb-4">{t("list.signals.signalCategorization")}</h3>
					<div className="space-y-4">
						<TextArea
							handleChange={(e) =>
								setSignalConfig((prev) => ({ ...prev, leadUpdatesPrompt: e.target.value }))
							}
							label={t("list.signals.leadUpdates")}
							labelClassName="font-semibold"
							name="leadUpdatesPrompt"
							placeholder={t("list.signals.leadUpdatesPlaceholder")}
							showError={false}
							value={signalConfig.leadUpdatesPrompt}
						/>
						<TextArea
							handleChange={(e) =>
								setSignalConfig((prev) => ({ ...prev, companyUpdatesPrompt: e.target.value }))
							}
							label={t("list.signals.companyUpdates")}
							labelClassName="font-semibold"
							name="companyUpdatesPrompt"
							placeholder={t("list.signals.companyUpdatesPlaceholder")}
							showError={false}
							value={signalConfig.companyUpdatesPrompt}
						/>
						<TextArea
							handleChange={(e) =>
								setSignalConfig((prev) => ({ ...prev, industryUpdatesPrompt: e.target.value }))
							}
							label={t("list.signals.industryUpdates")}
							labelClassName="font-semibold"
							name="industryUpdatesPrompt"
							placeholder={t("list.signals.industryUpdatesPlaceholder")}
							showError={false}
							value={signalConfig.industryUpdatesPrompt}
						/>
					</div>
				</div>

				{/* Priority Definitions Section */}
				<div className="p-4 mt-6 rounded-xl border border-border-light bg-brand-white">
					<h3 className="text-sm font-semibold mb-4">{t("list.signals.priorityDefinitions")}</h3>
					<p className="text-sm text-text-meta mb-6">
						{t("list.signals.priorityDefinitionsDescription")}
					</p>
					<PriorityDefinitionBoard
						className="grid grid-cols-1 lg:grid-cols-2 gap-6"
						items={signalConfig.priorities}
						onItemsChange={handleMovePriorityItem}
					>
						<PrioritySection
							movable
							items={getPriorityItems(SignalPriorityLevel.PRIORITY_1)}
							priorityLevel={SignalPriorityLevel.PRIORITY_1}
							onAddItem={handleAddPriorityItem}
							onDeleteItem={handleDeletePriorityItem}
							onEditItem={handleEditPriorityItem}
						/>
						<PrioritySection
							movable
							items={getPriorityItems(SignalPriorityLevel.PRIORITY_2)}
							priorityLevel={SignalPriorityLevel.PRIORITY_2}
							onAddItem={handleAddPriorityItem}
							onDeleteItem={handleDeletePriorityItem}
							onEditItem={handleEditPriorityItem}
						/>
						<PrioritySection
							movable
							items={getPriorityItems(SignalPriorityLevel.PRIORITY_3)}
							priorityLevel={SignalPriorityLevel.PRIORITY_3}
							onAddItem={handleAddPriorityItem}
							onDeleteItem={handleDeletePriorityItem}
							onEditItem={handleEditPriorityItem}
						/>
						<PrioritySection
							movable
							items={getPriorityItems(SignalPriorityLevel.DISQUALIFIED)}
							priorityLevel={SignalPriorityLevel.DISQUALIFIED}
							onAddItem={handleAddPriorityItem}
							onDeleteItem={handleDeletePriorityItem}
							onEditItem={handleEditPriorityItem}
						/>
					</PriorityDefinitionBoard>
				</div>
			</div>

			{/* Navigation Buttons */}
			<div className="absolute mt-14 h-[72px] flex flex-row justify-between items-center border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-20 bg-white w-full">
				<div className="flex flex-row items-center">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isTesting}
						isLoading={isTesting}
						title={t("list.signals.testConfiguration")}
						onClick={handleTest}
					/>
				</div>
				<div className="flex flex-row items-center gap-x-4">
					{isDirty && (
						<span className="inline-flex flex-col text-status-error text-xs whitespace-nowrap">
							You have unsaved changes
						</span>
					)}
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("basics.previous")}
						onClick={() => navigate(`/app/lists/create/source/${id}`)}
					/>
					<Button
						disabled={!isDirty || isSaving}
						isLoading={isSaving}
						title={t("basics.save")}
						onClick={handleSaveOnly}
					/>
					<Button
						disabled={isSaving}
						isLoading={isSaving}
						title={t("basics.next")}
						onClick={handleSave}
					/>
				</div>
			</div>

			{/* Priority Item Modal */}
			<PriorityItemModal
				key={modalKey}
				isOpen={modalOpen}
				item={editingItem}
				priorityLevel={editingPriorityLevel}
				onClose={() => setModalOpen(false)}
				onSave={handleSavePriorityItem}
			/>

			{/* Test Signal Config Modal */}
			<TestSignalConfigModal
				error={testError}
				isLoading={isTesting}
				isOpen={testModalOpen}
				result={testResult}
				onClose={() => setTestModalOpen(false)}
			/>
		</ListBuilderLayout>
	);
};
